import CommonDataGrid from "@/components/common/CommonDataGrid";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import type { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/date";
import { usePermissions } from "@/hooks/usePermissions";
import { useAppSelector } from "@/store/hooks";
import type { RootState } from "@/store/store";
import type {
  SiteDocumentationFilters,
  SiteDocumentationRow,
} from "@/types/siteDocumentation";

interface SiteDocumentationGridProps {
  filters: SiteDocumentationFilters | null;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onEditDocumentation: (rowId: string, allRows: SiteDocumentationRow[]) => void;
  onDeleteDocumentation: (
    rowId: string,
    allRows: SiteDocumentationRow[]
  ) => void;
  onBulkDelete: (allRows: SiteDocumentationRow[]) => void;
  onDownloadDocumentation: (rowId: string, allRows: any[]) => void;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSortChange?: (field: string | null, direction: 'asc' | 'desc' | null) => void;
}

const SiteDocumentationGrid: React.FC<SiteDocumentationGridProps> = ({
  filters,
  paginationModel,
  onPaginationModelChange,
  onEditDocumentation,
  onDeleteDocumentation,
  onBulkDelete,
  onDownloadDocumentation,
  sortField,
  sortDirection,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const { getFeatureActions } = usePermissions();

  // Get site documentation data from Redux store - no fetching here, just reading
  const {
    data: allRows,
    loading,
    error,
    totalCount,
  } = useAppSelector((state: RootState) => state.siteDocumentation);

  // Local UI state only
  const [rows, setRows] = useState<SiteDocumentationRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  // For server-side pagination, just use the data from Redux store
  // Filters will be handled by the backend in future if needed
  useEffect(() => {
    setRows(allRows);
    setSelectedRowIds([]); // Clear selection when data changes
  }, [allRows]);

  // Get current page row IDs for select-all-on-page
  const currentPageRowIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const allSelectedOnPage =
    currentPageRowIds.length > 0 &&
    currentPageRowIds.every((id) => selectedRowIds.includes(String(id)));
  const someSelectedOnPage = currentPageRowIds.some((id) =>
    selectedRowIds.includes(String(id))
  );

  // Individual row checkbox
  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    if (checked) {
      if (!selectedRowIds.includes(String(rowId))) {
        setSelectedRowIds([...selectedRowIds, String(rowId)]);
      }
    } else {
      setSelectedRowIds(selectedRowIds.filter((id) => id !== String(rowId)));
    }
  };

  // Header checkbox (select/deselect all on page)
  const handleHeaderCheckboxChange = (checked: boolean) => {
    if (!checked) {
      // Unchecking: remove all current page row IDs from selection
      setSelectedRowIds(
        selectedRowIds.filter(
          (id) => !currentPageRowIds.map(String).includes(String(id))
        )
      );
    } else {
      // Checking: add all current page row IDs
      const prevSet = new Set(selectedRowIds.map(String));
      const result = [...selectedRowIds.map(String)];
      for (let i = 0; i < currentPageRowIds.length; i++) {
        const idStr = String(currentPageRowIds[i]);
        if (!prevSet.has(idStr)) {
          result.push(idStr);
        }
      }
      setSelectedRowIds(result);
    }
  };

  const handleBulkDeleteClick = () => {
    console.log("Bulk delete clicked with selectedRowIds:", selectedRowIds);
    // Filter to get only the selected rows
    const selectedRows = allRows.filter((row) =>
      selectedRowIds.includes(String(row.id))
    );
    console.log("Selected rows for bulk delete:", selectedRows);
    onBulkDelete(selectedRows);
  };

  const handleClearSelection = () => {
    setSelectedRowIds([]);
  };

  // Custom checkbox column with header checkbox
  const checkboxColumn: GridColDef = {
    field: "select",
    headerName: "",
    renderHeader: () => {
      const indeterminate = !allSelectedOnPage && someSelectedOnPage;
      return (
        <Checkbox
          checked={allSelectedOnPage}
          indeterminate={indeterminate}
          onChange={(_, checked) => handleHeaderCheckboxChange(checked)}
          color={indeterminate ? "primary" : "secondary"}
          size="small"
          slotProps={{
            input: {
              "aria-label": `SiteDocumentation Select All Rows`,
            },
          }}
        />
      );
    },
    width: 80,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Checkbox
        checked={selectedRowIds.includes(String(params.row.id))}
        onChange={(_, checked) => handleCheckboxChange(params.row.id, checked)}
        color="secondary"
        size="small"
        slotProps={{
          input: {
            "aria-label": `Select row ${params.row.id}`,
          },
        }}
      />
    ),
  };

  // Actions column similar to turnaround implementation
  const actionsColumn = useMemo(
    () => ({
      headerName: t("actions", "ACTIONS"),
      actions: (row: any) => {
        const actions = [];
        if (selectedRowIds.length === 0) {
          actions.push({
            label: t("editDocumentationTitleCase", "Edit Document"),
            onClick: () => {
              console.log("Edit documentation with ID:", row.id, rows);
              onEditDocumentation(row.id, rows);
            },
          });
        }
        actions.push({
          label: t("deleteDocumentation", "Delete Document"),
          onClick: () => {
            console.log("Delete action clicked for row:", row.id, rows);
            onDeleteDocumentation(row.id, rows);
          },
        });
        actions.push({
          label: t("downloadDocumentation", "Download Document"),
          onClick: () => onDownloadDocumentation(row.id, rows),
        });
        return actions;
      },
      width: 140,
    }),
    [
      selectedRowIds.length,
      t,
      rows,
      onEditDocumentation,
      onDeleteDocumentation,
      onDownloadDocumentation,
    ]
  );

  const columns = useMemo(() => {
    const baseColumns: GridColDef[] = [
      checkboxColumn,
      {
        field: "name",
        headerName: t("documentName", "Document Name"),
        flex: 1,
        minWidth: 180,
        renderCell: (params) =>
          params.value && params.row.id ? (
            <span
              onClick={() => onDownloadDocumentation(params.row.id, rows)}
              className="site-doc-link text-primary"
              style={{ 
                textDecoration: "none", 
                color: "var(--primary)",
                cursor: "pointer"
              }}
            >
              {params.value}
            </span>
          ) : (
            "-"
          ),
      },
      {
        field: "type",
        headerName: t("type", "Type"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? params.value : "-"),
      },
      {
        field: "location",
        headerName: t("location", "Location"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? params.value : "-"),
      },
      {
        field: "report_date",
        headerName: t("reportDate", "Report Date"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? formatDate(params.value) : "-"),
      },
      {
        field: "upload_date",
        headerName: t("dateUploaded", "Date Uploaded"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? formatDate(params.value) : "-"),
      },
    ];
    return baseColumns;
  }, [checkboxColumn, t]);

  return (
    <>
      {selectedRowIds.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            mb: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="body2">
            {selectedRowIds.length}{" "}
            {selectedRowIds.length > 1
              ? t("documentsSelectedCount", "documents")
              : t("documentSelectedCount", "document")}{" "}
            {t("selected", "selected")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={handleBulkDeleteClick}
              data-testid="bulk-delete-button"
            >
              {t("deleteDocuments", "Delete Documents")}
            </Button>
            <Button variant="text" size="small" onClick={handleClearSelection}>
              {t("clearSelection", "Clear Selection")}
            </Button>
          </Box>
        </Box>
      )}
      <CommonDataGrid
        columns={columns}
        rows={rows}
        totalRowCount={totalCount}
        loading={loading}
        pageName="Site Documentation"
        error={error}
        paginationMode="server"
        sortingMode="server"
        hideHeaderCount={true}
        noRowsMessage={
          error ? error : t("noDocumentsFound", "No documents found.")
        }
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        hideFooterEntries={true}
        actionsColumn={actionsColumn}
        sortField={sortField || null}
        sortDirection={sortDirection || null}
        onSortChange={onSortChange}
      />
    </>
  );
};

export default SiteDocumentationGrid;
